import { UnresolvedSpecialCausesDataGridApi } from "src/api/generated/spc/controlChart/api/unresolvedSpecialCausesDataGridApi.ts";
import { ControlledAsyncDataGrid } from "src/components/common/dataGrid/ControlledAsyncDataGrid.tsx";
import { useServerSideDataGridModel } from "src/components/common/dataGrid/gridModel/useServerSideDataGridModel";
import { dateTimeColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { getSpecialCauseDescriptionForCode } from "src/components/views/spc/specialCauseUtils.ts";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import { useContext } from "react";
import { RawRecordView } from "src/api/generated/postgres/db/types/public_/tables/rawRecordView.ts";
import {
	FocusControlChartGraphViewToRecordButton,
	UploadDocumentsToSpcRecordButton,
} from "src/components/views/spc/controlChart/controlChartInspectingView/rawRecordDataGridUtils.tsx";
import { parseDayJsNullable } from "src/utils/dayjsUtils.ts";
import { AavoButton } from "src/components/common/buttons/AavoButton.tsx";
import { faCheck } from "@fortawesome/pro-regular-svg-icons";
import { SpcRecordActionForm } from "src/components/views/spc/controlChart/controlChartInspectingView/SpcRecordActionForm.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { ControlChartInspectingViewContext } from "src/components/views/spc/controlChart/controlChartInspectingView/ControlChartInspectingViewContext.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface UnresolvedSpecialCausesDataGridProps {
	controlChartId?: number;
	chartGroupId?: number;
}

export const UnresolvedSpecialCausesDataGrid = ({
	controlChartId,
	chartGroupId,
}: UnresolvedSpecialCausesDataGridProps) => {
	const { tenantConfig } = useTenantCustomizations();
	const { openDialog } = useGenericDialog();

	const { graphViewApiRef, eventsDataGridRefreshRef } = useContext(ControlChartInspectingViewContext) ?? {};

	const { dataGridProps, onlySelectedRow, selectedRows, refreshData } = useServerSideDataGridModel({
		gridId: "FD2EC55B3BC14A9BD",
		fetchData: (params) =>
			UnresolvedSpecialCausesDataGridApi.getGridData({
				controlChartId,
				chartGroupId,
				...params,
			}),
		initialParams: {},
		getRowId: (row) => row.recordId,
	});

	return (
		<ControlledAsyncDataGrid<RawRecordView>
			disableColumnFilter
			columns={[
				...(chartGroupId == null ?
					[]
				:	[
						textColumn<RawRecordView>({
							field: "controlChartName",
							headerName: i18n.t("control_chart"),
							sortable: false,
							width: 200,
						}),
					]),
				dateTimeColumn({
					field: "observationTime",
					headerName: i18n.t("recorded_at"),
					sortable: false,
					width: 150,
				}),
				textColumn({
					field: "specialCauseCode",
					headerName: i18n.t("special_cause"),
					valueGetter: (_, row) =>
						row.specialCauseCode == null ? "" : getSpecialCauseDescriptionForCode(row.specialCauseCode),
					sortable: false,
					width: 600,
				}),
				integerColumn({
					field: "recordId",
					headerName: i18n.t("id"),
					sortable: false,
				}),
			]}
			actionBarComponents={
				<>
					{tenantConfig.documentsEnabled && (
						<>
							<DocumentsOfObjectButton
								objectRef={nullableAavoObjectRef("SPC_RECORD", onlySelectedRow?.recordId)}
							/>
							<UploadDocumentsToSpcRecordButton
								disabled={!onlySelectedRow}
								recordId={onlySelectedRow?.recordId}
							/>
						</>
					)}
					{controlChartId != null && (
						<FocusControlChartGraphViewToRecordButton
							disabled={!onlySelectedRow}
							recordObservationTime={parseDayJsNullable(onlySelectedRow?.observationTime)}
							graphViewApiRef={graphViewApiRef}
						/>
					)}
					<AavoButton
						label={i18n.t("spc_action")}
						icon={faCheck}
						variant={"outlined"}
						disabled={selectedRows.length === 0}
						onClick={() => {
							openDialog(({ closeDialog, onContentEdited }) => ({
								title: i18n.t("spc_action"),
								size: "sm",
								agentContext: {
									key: defineAgentUiContextKey(
										"spc.controlChart.recordAction.unresolvedSpecialCausesDataGrid.dialog",
									),
									data: {
										controlChartId: controlChartId ?? null,
										chartGroupId: chartGroupId ?? null,
										recordIds: selectedRows.map((row) => row.recordId),
									},
									pathSegments: [i18n.t("spc_action")],
								},
								content: (
									<SpcRecordActionForm
										recordIds={selectedRows.map((row) => row.recordId)}
										onFormEdited={onContentEdited}
										onCompleted={async () => {
											await closeDialog();
											await Promise.all([
												refreshData(),
												graphViewApiRef?.current?.refresh(),
												eventsDataGridRefreshRef?.refresh(),
											]);
										}}
									/>
								),
							}));
						}}
					/>
				</>
			}
			agentContext={{
				key: defineAgentUiContextKey("spc.controlChart.unresolvedSpecialCauses.grid"),
				data: { controlChartId: controlChartId ?? null, chartGroupId: chartGroupId ?? null },
				rowFields: ["recordId", "controlChartId", "specialCauseCode"],
				selectedRowPathSegment: (row) => `SPC ${row.recordId}`,
				selectedRowsPathSegment: (count) => i18n.t("rows_selected_count", { count }),
			}}
			{...dataGridProps}
		/>
	);
};
